/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2017
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/dashboard-common/dist/core/Collection',
	'../../lib/@waca/core-client/js/core-client/errors/BaseError'
], function(Collection, BaseError) {

	/**
	 * A collection which is created from the server. For example, if we had a server collection
	 * called ThingsCollection and it specified a URL of /things then if we did:
	 *
	 * new ThingsCollection().fetch();
	 *
	 * Then that collection would make a call to:
	 *
	 * GET /things
	 *
	 * And materialize the collection from the results of that call.
	 *
	 * Overriders of this class should define a url property, e.g:
	 *
	 * ThingsCollection = ServerCollection.extend({
	 *
	 * 	url: '/things',
	 *	init: function() {
	 *		...
	 *	},
	 *
	 *	...
	 * });
	 *
	 */
	const ServerCollection = Collection.extend({

		init: function(models, options) {
			ServerCollection.inherited('init', this, arguments);
			this._ajaxSvc = options.ajaxSvc;
		},

		/**
		 * Get all the models in the collection from the server. Returns a promise which
		 * is resolved when the collection has been reset with the result from the server.
		 */
		fetch: function() {
			return new Promise((resolve, reject) => {
				this._ajaxSvc
					.ajax({
						url: this.url,
						type: 'GET',
						cache: false,
						headers: {
							Accept: 'application/json'
						}
					})
					.then((models) => {
						this.reset(models);
						resolve(models);
					})
					.catch(error => {
						reject(new BaseError(error));
					});
			});
		},

		/**
		 * Fetch a model with the given ID and add it to the collection
		 */
		fetchModel: function(id) {
			return new Promise((resolve, reject) => {
				this._ajaxSvc
					.ajax({
						url: `${this.url}/${id}`,
						type: 'GET',
						headers: { Accept: 'application/json' }
					})
					.then((model) => {
						if (model) {
							this.add(model);
						}
						resolve(this.get(model));
					})
					.catch(error => {
						reject(new BaseError(error));
					});
			});
		},

		/**
		 * Adds a new model to the collection by POSTing it to the collection.
		 *
		 */
		addModel: function(model) {
			return new Promise((resolve, reject) => {
				this._ajaxSvc
					.ajax({
						url: this.url,
						type: 'POST',
						contentType: 'application/json',
						processData: false,
						dataType: 'json',
						data: JSON.stringify(model),
						headers: { Accept: 'application/json' }
					})
					.then((serverModel) => {
						if ( serverModel ) {
							this.add(serverModel);
						}
						resolve(this.get(serverModel));
					})
					.catch(error => {
						reject(new BaseError(error));
					});
			});
		}

	});

	return ServerCollection;
});
